import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  IconButton,
  TableRow,
  TableCell,
  TextField,
  Typography,
  FormHelperText,
  Button
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch, useSelector } from "react-redux";

import {
  getDifferentiatedValues,
  getUser,
  getUserIndex,
  isOnlyAndMainEmployee
} from "../../../../utils/HealthDeclarationForm.utils";

import { FemaleSpecificClass } from "../../../../model/FemaleSpecific.class";

import { selectHealthDeclarationOfUsers } from "../../../../redux/health-declaration/hdf.selectors";
import { handleUpdateHealthDeclaration } from "../../../../redux/health-declaration/hdf.actions";

import { questionnairesStyles, saveButton } from "../questionnaire.styles";

import { DatePickerComponent } from "../../date-picker/DatePicker.component";

export const PAPSmearQuestionnaire = props => {
  const {
    container,
    username,
    tableCell,
    infoGrid,
    textFieldRed
  } = questionnairesStyles();
  const { id } = props;

  const [testDate, setTestDate] = useState("");
  const [testResult, setTestResult] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [treatmentType, setTreatmentType] = useState("");
  const [
    nameAddressOfDoctorConsulted,
    setNameAddressOfDoctorConsulted
  ] = useState(undefined);

  const [testDateError, setTestDateError] = useState(false);
  const [testResultError, setTestResultError] = useState(false);
  const [diagnosisError, setDiagnosisError] = useState(false);
  const [treatmentTypeError, setTreatmentTypeError] = useState(false);
  const [
    nameAddressOfDoctorConsultedError,
    setNameAddressOfDoctorConsultedError
  ] = useState(false);

  const healthDeclarationOfUsers = useSelector(selectHealthDeclarationOfUsers);
  const dispatch = useDispatch();

  const { userId, key, subKey } = getDifferentiatedValues(id);

  const userIndex = getUserIndex(healthDeclarationOfUsers, userId);
  const user = getUser(healthDeclarationOfUsers, userId);

  useEffect(() => {
    user?.femaleInsuredOnly?.papSmear?.testDate &&
      setTestDate(user.femaleInsuredOnly.papSmear.testDate);
    user?.femaleInsuredOnly?.papSmear?.testResult &&
      setTestResult(user.femaleInsuredOnly.papSmear.testResult);
    user?.femaleInsuredOnly?.papSmear?.diagnosis &&
      setDiagnosis(user.femaleInsuredOnly.papSmear.diagnosis);
    user?.femaleInsuredOnly?.papSmear?.treatmentType &&
      setTreatmentType(user.femaleInsuredOnly.papSmear.treatmentType);
    user?.femaleInsuredOnly?.papSmear?.nameAddressOfDoctorConsulted &&
      setNameAddressOfDoctorConsulted(
        user.femaleInsuredOnly.papSmear.nameAddressOfDoctorConsulted
      );

    let validateSuccess = validateFields();
    if (user?.femaleInsuredOnly?.papSmear || validateSuccess) {
      validateSuccess = true;
    }
    props.handleRequiredFieldsError(key, subKey, !validateSuccess);
  }, []);

  useEffect(() => {
    validateFields();
  }),
    [
      testDate,
      testResult,
      diagnosis,
      treatmentType,
      nameAddressOfDoctorConsulted
    ];

  const validateFields = () => {
    let validateSuccess = true;

    setTestDateError(!testDate);
    setTestResultError(!testResult);
    setDiagnosisError(!diagnosis);
    setTreatmentTypeError(!treatmentType);
    setNameAddressOfDoctorConsultedError(!nameAddressOfDoctorConsulted);

    if (
      !testDate ||
      !testResult ||
      !diagnosis ||
      !treatmentType ||
      !nameAddressOfDoctorConsulted
    ) {
      validateSuccess = false;
    }

    return validateSuccess;
  };

  const updateUserHealthDeclaration = () => {
    let validationResult = validateFields();

    if (validationResult) {
      props.handleRequiredFieldsError(key, subKey, false);
      const femaleSpecific = new FemaleSpecificClass(
        testDate,
        undefined,
        testResult,
        diagnosis,
        treatmentType,
        nameAddressOfDoctorConsulted
      );
      const femaleSpecificObject = {};
      femaleSpecificObject[subKey] = femaleSpecific.getFemaleSpecificDetails();

      const updatedUser = {
        ...user,
        [key]: { ...user[key], ...femaleSpecificObject }
      };

      healthDeclarationOfUsers[userIndex] = updatedUser;
      dispatch(handleUpdateHealthDeclaration(healthDeclarationOfUsers));
    }
  };

  return (
    <Box className={container}>
      <Typography className={username}>
        {isOnlyAndMainEmployee(healthDeclarationOfUsers) ? "" : user?.full_name}
        {isOnlyAndMainEmployee(healthDeclarationOfUsers)
          ? ""
          : user.person_type === "EMPLOYEE"
          ? "(Self)"
          : user.person_type === "SPOUSE"
          ? "(Spouse)"
          : "(Child)"}
      </Typography>
      <TableRow>
        <TableCell className={tableCell}>
          <Grid container item spacing={"2px"} flexDirection={"column"}>
            <Grid item xs={5} className={infoGrid}>
              <Box fontWeight="bold">Date of test done</Box>
            </Grid>
            <Grid item xs={7} className={infoGrid}>
              <DatePickerComponent
                key={testDate}
                date={testDate}
                onDateChange={formattedDate => {
                  setTestDate(formattedDate);
                }}
              />
              {testDateError && (
                <FormHelperText style={{ color: "red" }}>
                  This field is mandatory.
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </TableCell>
        <TableCell className={tableCell}>
          <Grid container item spacing={"2px"} flexDirection={"column"}>
            <Grid item xs={5} className={infoGrid}>
              <Box fontWeight="bold">Result of the test done</Box>
            </Grid>
            <Grid item xs={7} className={infoGrid}>
              <TextField
                id="standard-basic"
                variant="standard"
                className={textFieldRed}
                required
                error={testResultError}
                helperText={testResultError && "This field is mandatory."}
                onChange={event => {
                  setTestResult(event.target.value);
                }}
                value={testResult}
              />
            </Grid>
          </Grid>
        </TableCell>
        <TableCell className={tableCell}>
          <Grid container item spacing={"2px"} flexDirection={"column"}>
            <Grid item xs={5} className={infoGrid}>
              <Box fontWeight="bold">Diagnosis</Box>
            </Grid>
            <Grid item xs={7} className={infoGrid}>
              <TextField
                id="standard-basic"
                variant="standard"
                className={textFieldRed}
                required
                error={diagnosisError}
                helperText={diagnosisError && "This field is mandatory."}
                onChange={event => {
                  setDiagnosis(event.target.value);
                }}
                value={diagnosis}
              />
            </Grid>
          </Grid>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell className={tableCell}>
          <Grid container item spacing={"2px"} flexDirection={"column"}>
            <Grid item xs={5} className={infoGrid}>
              <Box fontWeight="bold">Type of Treatment</Box>
            </Grid>
            <Grid item xs={7} className={infoGrid}>
              <TextField
                id="standard-basic"
                variant="standard"
                className={textFieldRed}
                required
                error={treatmentTypeError}
                helperText={treatmentTypeError && "This field is mandatory."}
                onChange={event => {
                  setTreatmentType(event.target.value);
                }}
                value={treatmentType}
              />
            </Grid>
          </Grid>
        </TableCell>
        <TableCell className={tableCell}>
          <Grid container item spacing={"2px"} flexDirection={"column"}>
            <Grid item xs={5} className={infoGrid}>
              <Box fontWeight="bold">
                Name & Address of Doctors / Clinics / Hospitals
              </Box>
            </Grid>
            <Grid item xs={7} className={infoGrid}>
              <TextField
                id="standard-basic"
                variant="standard"
                className={textFieldRed}
                required
                error={nameAddressOfDoctorConsultedError}
                helperText={
                  nameAddressOfDoctorConsultedError &&
                  "This field is mandatory."
                }
                onChange={event => {
                  setNameAddressOfDoctorConsulted(event.target.value);
                }}
                value={nameAddressOfDoctorConsulted}
              />
            </Grid>
          </Grid>
        </TableCell>
      </TableRow>
      <TableRow>
        <Grid item xs={5} className={infoGrid}>
          <Box fontWeight="bold">
            Please upload a copy of Medical Report if it is available.
          </Box>
        </Grid>
      </TableRow>
      <Button
        variant="outlined"
        sx={{ ...saveButton }}
        onClick={updateUserHealthDeclaration}
      >
        Save
      </Button>
    </Box>
  );
};
